import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import {
  createForexPaymentLink,
  getForexPaymentLinkStatus,
  type PaymentLink,
} from "service/moneeflo/legacy-forex-desk-api";
import DeskDialog, { inputClass } from "./DeskDialog";
import { formatInr, formatInrMinor } from "../utils/money";

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

/**
 * A link the customer pays this order through. Core makes it and returns the
 * address once, so it is shown here with a Copy button; it can also be emailed
 * to the customer. Nothing here says the customer has paid: that only happens
 * when the payment reaches the order.
 */
export function PaymentLinkDialog({
  open,
  onOpenChange,
  amount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rupees still to pay. */
  amount: number;
}) {
  const [sendEmail, setSendEmail] = useState(false);
  const [link, setLink] = useState<PaymentLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setSendEmail(false);
      setLink(null);
      setError(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: createForexPaymentLink,
    onSuccess: (created) => {
      setLink(created);
      queryClient.invalidateQueries(["paymentLinkStatus"]);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The payment link could not be created."),
  });

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Link copied");
    } catch {
      toast.error("The link could not be copied. Select it and copy it by hand.");
    }
  };

  return (
    <DeskDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Payment link"
      busy={create.isLoading}
      submitLabel="Create link"
      hideSubmit={!!link}
      closeLabel={link ? "Done" : "Cancel"}
      error={error}
      onSubmit={() => {
        setError(null);
        create.mutate(sendEmail);
      }}
    >
      {link ? (
        <>
          <p className="text-sm text-[#333333]">
            {link.emailSent ? "The link was emailed to the customer. " : "The link is ready. "}
            It is valid until {when(link.expiresAt)}.
          </p>
          <div className="flex gap-2">
            <input
              readOnly
              aria-label="Payment link"
              value={link.url}
              onFocus={(e) => e.currentTarget.select()}
              className={inputClass}
            />
            <button
              type="button"
              onClick={copy}
              className="shrink-0 cursor-pointer rounded-lg border border-[#165FE3] px-4 py-2 text-sm font-medium text-[#165FE3] hover:bg-[#F0F4FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#165FE3]"
            >
              Copy link
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[#333333]">
            A link the customer can pay{" "}
            <span className="font-semibold text-[#165FE3]">{formatInr(amount)}</span> through.
          </p>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-[#333333]">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
            Email it to the customer too
          </label>
        </>
      )}
    </DeskDialog>
  );
}

/**
 * Where the order's payment link stands, read from core: made, emailed, opened,
 * expired, and what payment attempts came of it.
 */
export function PaymentLinkStatusPanel({ orderRef, canReissue = true }: { orderRef: string; canReissue?: boolean }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["paymentLinkStatus", orderRef],
    queryFn: () => getForexPaymentLinkStatus(orderRef),
    // A finance user may read payments but not the desk's link: nothing to show then.
    retry: false,
    refetchOnWindowFocus: false,
  });
  // Core shows a link's address only when it is made, so Copy and Resend each make a fresh link, which retires the earlier ones.
  const fresh = useMutation((sendEmail: boolean) => createForexPaymentLink(sendEmail), {
    onSuccess: async (created: PaymentLink, sendEmail: boolean) => {
      queryClient.invalidateQueries(["paymentLinkStatus"]);
      if (sendEmail) {
        toast.success(created.emailSent ? "Link emailed to the customer" : "A new link was made");
        return;
      }
      try {
        await navigator.clipboard.writeText(created.url);
        toast.success("New link copied");
      } catch {
        toast.error("The link could not be copied.");
      }
    },
    onError: (e: unknown) => {
      toast.error("The link could not be made", { description: e instanceof Error ? e.message : undefined });
    },
  });
  if (!data?.link) return null;
  const { link, attempts } = data;
  const state = link.expired ? "expired" : attempts.some((a) => a.status === "success" || a.status === "paid") ? "paid" : "pending";
  return (
    <div data-testid="payment-link-status" className="flex flex-wrap items-center justify-between gap-2 break-words rounded-lg border border-[#E1EBFC] bg-[#F4F8FF] px-4 py-3 text-sm text-[#262F63]">
      <p className="min-w-0 break-words">
        <span className="font-medium">Link for {formatInrMinor(link.amountMinor)}</span>
        {link.expired ? "" : ` · valid until ${when(link.expiresAt)}`}
        {` · opened ${link.openCount} ${link.openCount === 1 ? "time" : "times"}`}
        {` · ${state}`}
      </p>
      {canReissue ? <p className="w-full text-xs text-[#4A5578]">Making a new link stops the previous one working.</p> : null}
      <div className={canReissue ? "flex flex-wrap gap-2" : "hidden"}>
        <button
          type="button"
          disabled={fresh.isLoading}
          onClick={() => fresh.mutate(false)}
          className="cursor-pointer rounded-lg border border-[#165FE3] px-3 py-1.5 font-medium text-[#165FE3] hover:bg-[#F0F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Copy new link
        </button>
        {link.emailedTo ? (
          <button
            type="button"
            disabled={fresh.isLoading}
            onClick={() => fresh.mutate(true)}
            className="cursor-pointer rounded-lg border border-[#165FE3] px-3 py-1.5 font-medium text-[#165FE3] hover:bg-[#F0F4FF] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Resend
          </button>
        ) : null}
      </div>
    </div>
  );
}
